import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import { updateProductAttribute } from '../services/catalog-operations.service';

/** One attribute override submitted by the operator. */
export interface UpdateAttributeVariables {
  /** Optimization wire field name, e.g. `product_title`. */
  field: string;
  /** Human label, used only for the toast copy. */
  label: string;
  /** JSON in the attribute's own shape — object, array, or string. */
  value: unknown;
}

/**
 * Saves an operator edit to a single enriched attribute, then refetches the
 * product's enrichment payload so the row re-renders from the server's copy
 * rather than the local draft.
 *
 * The refetch is what makes the row truthful rather than optimistic: the service
 * echoes the value as persisted, which differs from what was sent whenever a
 * coercion applied, so re-reading is cheaper than mirroring the server's coercion
 * rules here.
 *
 * Two reads are invalidated, because a save produces two changes: the enrichment
 * payload carries the new value, and the attribute-edits list carries the new
 * provenance row. Invalidating only the first leaves the "edited by" marker a
 * refresh behind its own edit.
 *
 * The edit does not re-score the product and does not invalidate gap analysis —
 * the score shown above these attributes keeps the agent's value by design, so
 * neither query is invalidated here.
 */
export function useUpdateProductAttribute(productId: string, connectorCode?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ field, value }: UpdateAttributeVariables) =>
      updateProductAttribute(productId, { field, value, connectorCode }),
    onSuccess: async (_data, variables) => {
      toast.success(`Saved ${variables.label}.`);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.catalogOperations.productEnrichment(productId, false, connectorCode),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.catalogOperations.productAttributeEdits(productId, connectorCode),
        }),
      ]);
    },
    onError: (error) => {
      toast.error(extractApiError(error, 'Could not save the attribute. Please try again.'));
    },
  });
}
